/**
 * Combat resolution: scan-and-shoot, damage application, base-strike rewards.
 *
 * The original distinguishes "right weapon vs wrong weapon" for the player but
 * the AI always damages on a clean line of sight (MECHANICS.md §10.5). We
 * model both with a single `tryDamage` and let the caller decide whether to
 * gate by weapon match.
 */

import { chebyshev } from "./types.js";
import { type Unit, UNIT_DATA, baseStrikeScore, killScore } from "./units.js";

/** Result of one attack attempt — useful for logging, AI feedback, audio. */
export interface AttackResult {
  /** True if the shot connected with a target. */
  hit: boolean;
  /** True if the target had a shield up that absorbed the hit. */
  absorbed: boolean;
  /** True if the target died from this hit. */
  killed: boolean;
  /** Score earned by the attacker (0 if missed/absorbed/can't-kill). */
  score: number;
  /** Target unit, or null if no valid target was in range. */
  target: Unit | null;
}

/**
 * Apply a single hit to `target` from `attacker`. Returns details for
 * the caller (UI / audio / scoring).
 */
export function applyHit(
  _attacker: Unit,
  target: Unit,
  difficulty: number,
): AttackResult {
  if (!target.alive) {
    return { hit: false, absorbed: false, killed: false, score: 0, target };
  }
  if (target.shieldTicks > 0) {
    target.shieldTicks -= 1;
    return { hit: true, absorbed: true, killed: false, score: 0, target };
  }
  target.hp -= 1;
  if (target.hp > 0) {
    return { hit: true, absorbed: false, killed: false, score: 0, target };
  }
  target.alive = false;
  return {
    hit: true,
    absorbed: false,
    killed: true,
    score: killScore(target.kind, difficulty),
    target,
  };
}

/**
 * Predicate for "can this attacker class see/hit this target class".
 * Mirrors the per-class sight-set rules in MECHANICS.md §10.5 for the AI's
 * soldier/tank/cruiser. For other attacker kinds we default to "anyone".
 */
export function targetClassVisible(attackerKind: number, targetKind: number): boolean {
  // 3 = Soldier, 6 = Tank, 8 = Cruiser
  if (attackerKind === 3) return targetKind <= 5;
  if (attackerKind === 6) return targetKind === 3 || targetKind === 6 || targetKind === 7;
  if (attackerKind === 8) return targetKind >= 6;
  // Static guns (4) and rocket plants (5) target any moving unit (best
  // observable from the original asset layout: per-class lose_X sounds).
  return true;
}

/**
 * Find the closest visible enemy unit within `attacker`'s sight range.
 * Returns null if no eligible target exists.
 */
export function findTarget(
  attacker: Unit,
  enemyUnits: readonly Unit[],
): Unit | null {
  const range = UNIT_DATA[attacker.kind].sightRange;
  if (range === 0) return null;
  let best: Unit | null = null;
  let bestDist = range + 1;
  for (const u of enemyUnits) {
    if (!u.alive) continue;
    if (u.owner === attacker.owner) continue;
    if (!targetClassVisible(attacker.kind, u.kind)) continue;
    const d = chebyshev(attacker.position, u.position);
    if (d > range) continue;
    if (d < bestDist) {
      bestDist = d;
      best = u;
    }
  }
  return best;
}

/**
 * Calculate the score (and trigger the side-effect intent) for `attacker`
 * arriving on the enemy base cell. Returns the score and a flag for
 * special side-effects the caller must apply.
 */
export interface BaseStrike {
  score: number;
  /** If true, the AI tank just hit the player base — wipe player's workers + trucks. */
  wipeLightUnits: boolean;
  /** If true, the AI cruiser just hit the player base — game is over (loss). */
  gameOver: boolean;
}

export function resolveBaseStrike(
  attacker: Unit,
  difficulty: number,
): BaseStrike {
  return {
    score: baseStrikeScore(attacker.kind, difficulty),
    wipeLightUnits: attacker.kind === 6, // Tank
    gameOver: attacker.kind === 8, // Cruiser
  };
}
